import React, { useEffect, useId, useRef, useState } from "react"
import { cn } from "../utils"
import { useRender } from "@base-ui/react/use-render"
import { VisuallyHidden } from "../visually-hidden"
import { Icon } from "../icon"
import { Tooltip } from "../tooltip"
import { useIsTruncated } from "../use-is-truncated"

// =============================================================================
// Props
// =============================================================================

type PageSecondaryHeaderBreadcrumbCommonProps = {
  className?: string
  /**
   * Rendered beside the breadcrumb text — a status dot, a badge. Kept out of
   * `children` so it stays clear of the truncation measurement and of the
   * tooltip, back button and rename label that reuse the text.
   */
  adornment?: React.ReactNode
  /**
   * Whether this breadcrumb represents the current page. Set automatically by
   * `PageSecondaryHeader.Breadcrumbs` on the last child; consumers should not
   * pass this directly.
   */
  isCurrent?: boolean
  /**
   * Whether this is the first breadcrumb. Set automatically by
   * `PageSecondaryHeader.Breadcrumbs`; consumers should not pass this directly.
   * Below `@3xl/page-header` the first breadcrumb collapses into a back button.
   */
  isFirst?: boolean
  /**
   * Whether this breadcrumb sits between the first and last. Set automatically
   * by `PageSecondaryHeader.Breadcrumbs`; consumers should not pass this
   * directly. Hidden below `@3xl/page-header`.
   */
  isInterior?: boolean
}

type PageSecondaryHeaderBreadcrumbLinkProps =
  PageSecondaryHeaderBreadcrumbCommonProps &
    Omit<
      React.AnchorHTMLAttributes<HTMLAnchorElement>,
      "onChange" | "children"
    > & {
      /** Breadcrumb href. Renders an `<a>` when provided. */
      href: string
      /**
       * Replace the rendered element so consumers can supply their own element.
       * Defaults to an `<a>` when `href` is provided, otherwise a `<button>`.
       * The last breadcrumb renders as static text.
       */
      render?: useRender.RenderProp
    }

type PageSecondaryHeaderBreadcrumbButtonProps =
  PageSecondaryHeaderBreadcrumbCommonProps &
    Omit<
      React.ButtonHTMLAttributes<HTMLButtonElement>,
      "onChange" | "value" | "children"
    > & {
      /** Breadcrumb href. Renders a `<button>` when omitted. */
      href?: undefined
      render?: useRender.RenderProp
    }

type PageSecondaryHeaderBreadcrumbEditableExtension =
  | {
      /** When `true`, renders an edit icon button that triggers an inline input. */
      editable: true
      /** Current text displayed by the breadcrumb. Required to be a string in editable mode. */
      children: string
      /** Called with the trimmed new value when the user commits an edit (Enter or blur). */
      onChange: (value: string) => void
    }
  | {
      editable?: false
      children?: React.ReactNode
      onChange?: undefined
    }

export type PageSecondaryHeaderBreadcrumbProps = (
  | PageSecondaryHeaderBreadcrumbLinkProps
  | PageSecondaryHeaderBreadcrumbButtonProps
) &
  PageSecondaryHeaderBreadcrumbEditableExtension

// =============================================================================
// Styles
// =============================================================================

const pageSecondaryHeaderBreadcrumbStyles = [
  "inline-block",
  "outline-2 outline-offset-2 outline-transparent",
  "rounded-sm",
  "max-w-32 data-current:max-w-none data-current:w-full",
  "py-1 px-2 data-current:pr-0",
  "text-sm font-semibold text-gray-700 leading-6 truncate data-current:text-gray-900",
  "transition-colors",
  "hover:bg-gray-100 hover:text-gray-900 data-current:hover:bg-transparent data-current:hover:text-gray-900 focus-visible:outline-gray-900"
]

const pageSecondaryHeaderBreadcrumbBackButtonStyles = [
  "outline-2 outline-transparent",
  "rounded-sm",
  "w-8 h-8",
  "flex items-center justify-center",
  "text-gray-700",
  "transition-colors",
  "hover:bg-gray-100 hover:text-gray-900 focus-visible:outline-gray-900"
]

const pageSecondaryHeaderBreadcrumbEditIconButtonStyles = [
  "outline-2 outline-transparent",
  "rounded-sm bg-white",
  "w-8 h-8 shrink-0",
  "flex items-center justify-center",
  "text-gray-900",
  "transition-colors",
  "hover:bg-gray-100 focus:outline-gray-900"
]

const pageSecondaryHeaderBreadcrumbInputStyles = [
  "outline-2 outline-offset-2 outline-transparent",
  "ring ring-gray-200 rounded-sm bg-white",
  "w-full",
  "py-1 px-2",
  "text-sm font-semibold text-gray-900 leading-6",
  "focus:outline-gray-900"
]

// =============================================================================
// Component
// =============================================================================

/**
 * Secondary page header breadcrumb.
 *
 * Renders an `<a>` (when `href` is provided) or a `<button>`, wrapped in an
 * `<li>` with the breadcrumb divider. The last breadcrumb is automatically
 * marked as the current breadcrumb and is inactive.
 *
 * Below `@3xl/page-header` the first breadcrumb collapses into an icon-only back
 * button and interior breadcrumbs are hidden, leaving the current breadcrumb as
 * the only visible text breadcrumb.
 *
 * Breadcrumbs truncate at their max-width. Any breadcrumb whose text is
 * actually clipped reveals a tooltip with the full text on hover/focus.
 *
 * When `editable` is `true`, an edit icon button is rendered that triggers an
 * inline input. Editable breadcrumbs accept `onChange` to handle the user's
 * input.
 */
export function PageSecondaryHeaderBreadcrumb({
  adornment,
  className,
  children,
  editable,
  isCurrent,
  isFirst,
  isInterior,
  onChange,
  render,
  ...props
}: PageSecondaryHeaderBreadcrumbProps) {
  const [isEditing, setIsEditing] = useState(false)
  // `editable` guarantees `children` is a string at the type level.
  const text = editable ? (children as string) : ""
  const [draft, setDraft] = useState(text)
  const inputId = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const editButtonRef = useRef<HTMLButtonElement>(null)
  const restoreFocusOnExitRef = useRef(false)
  // Any breadcrumb can truncate at its max-width. Only surface a tooltip with
  // the full text while it is actually clipped — otherwise the tooltip would
  // redundantly repeat text that is already fully visible.
  const { ref: textRef, isTruncated } = useIsTruncated<HTMLElement>()

  // Sync external text changes when the user isn't actively editing — otherwise
  // an unrelated re-render with the same value would wipe in-progress input.
  useEffect(() => {
    if (!isEditing) setDraft(text)
  }, [text, isEditing])

  // Focus and select the input text when entering edit mode so the user can
  // immediately type over the current value.
  useEffect(() => {
    if (!isEditing) return
    inputRef.current?.focus()
    inputRef.current?.select()
  }, [isEditing])

  // Return focus to the edit button after exiting edit mode via Escape. Done in
  // an effect (rather than calling focus() directly in `cancel`) so the input
  // has already unmounted — otherwise the synchronous focus shift would fire
  // `onBlur` and accidentally trigger a commit.
  useEffect(() => {
    if (isEditing || !restoreFocusOnExitRef.current) return
    restoreFocusOnExitRef.current = false
    editButtonRef.current?.focus()
  }, [isEditing])

  const commit = () => {
    setIsEditing(false)
    if (!editable || !onChange) return
    const trimmed = draft.trim()
    if (trimmed === "") {
      setDraft(text)
      return
    }
    if (trimmed !== text) onChange(trimmed)
  }

  const cancel = () => {
    setDraft(text)
    setIsEditing(false)
    restoreFocusOnExitRef.current = true
  }

  // Persist a pending edit if the user closes/refreshes the page mid-edit
  // (blur normally handles intra-app navigation).
  useEffect(() => {
    if (!isEditing || !editable || !onChange) return
    const handleUnload = () => {
      const trimmed = draft.trim()
      if (trimmed !== "" && trimmed !== text) onChange(trimmed)
    }
    window.addEventListener("beforeunload", handleUnload)
    return () => window.removeEventListener("beforeunload", handleUnload)
  }, [isEditing, editable, onChange, draft, text])

  const renderProps = isCurrent
    ? {
        defaultTagName: "span" as const,
        props: {
          children,
          className: cn(pageSecondaryHeaderBreadcrumbStyles, className),
          "aria-hidden": true,
          "data-current": ""
        }
      }
    : props.href !== undefined
      ? {
          defaultTagName: "a" as const,
          props: {
            children,
            className: cn(pageSecondaryHeaderBreadcrumbStyles, className),
            ...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)
          }
        }
      : {
          defaultTagName: "button" as const,
          props: {
            children,
            className: cn(pageSecondaryHeaderBreadcrumbStyles, className),
            type: "button" as const,
            ...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)
          }
        }

  const breadcrumbElement = useRender({
    ...renderProps,
    render,
    // Measure the rendered breadcrumb to drive the truncation tooltip.
    ref: textRef
  })

  // Below `@3xl/page-header` the first breadcrumb collapses into a back button
  // (the last breadcrumb stays as the only visible text breadcrumb). It mirrors
  // the breadcrumb's `href`/`render`/handlers so navigation behaves identically.
  const showBackButton = isFirst && !isCurrent
  const backButtonRenderProps =
    props.href !== undefined
      ? {
          defaultTagName: "a" as const,
          props: {
            className: cn(
              pageSecondaryHeaderBreadcrumbBackButtonStyles,
              className
            ),
            ...(props as React.AnchorHTMLAttributes<HTMLAnchorElement>)
          }
        }
      : {
          defaultTagName: "button" as const,
          props: {
            className: cn(
              pageSecondaryHeaderBreadcrumbBackButtonStyles,
              className
            ),
            type: "button" as const,
            ...(props as React.ButtonHTMLAttributes<HTMLButtonElement>)
          }
        }
  const backButtonElement = useRender({
    ...backButtonRenderProps,
    props: {
      ...backButtonRenderProps.props,
      children: (
        <>
          <VisuallyHidden>{children}</VisuallyHidden>
          <Icon name="arrow-left" size="md" />
        </>
      )
    },
    render
  })

  return (
    <div
      className={cn(
        "flex items-center first-of-type:[&>svg]:hidden",
        // Interior breadcrumbs are hidden below the container breakpoint,
        // leaving only the back button and the current breadcrumb.
        isInterior && "hidden @3xl/page-header:flex",
        // Hide the divider before the current breadcrumb below the breakpoint —
        // there is no divider between the back button and the current breadcrumb.
        isCurrent && "[&>svg]:hidden @3xl/page-header:[&>svg]:inline-block",
        isCurrent && "min-w-0 grow"
      )}
    >
      <Icon name="slash" size="md" className="text-gray-700" />
      {showBackButton && (
        // Back button shown below the container breakpoint.
        <div className="flex items-center @3xl/page-header:hidden">
          <Tooltip>
            <Tooltip.Trigger render={backButtonElement} />
            <Tooltip.Content>Back to {children}</Tooltip.Content>
          </Tooltip>
        </div>
      )}
      <div
        className={cn(
          "relative flex items-center whitespace-nowrap",
          isCurrent && "min-w-0",
          isCurrent && (isTruncated || isEditing) && "grow",
          // Full breadcrumb shown above the breakpoint when a back button
          // replaces it below.
          showBackButton && "hidden @3xl/page-header:flex"
        )}
      >
        <Tooltip disabled={!isTruncated}>
          <Tooltip.Trigger render={breadcrumbElement} />
          <Tooltip.Content>{children}</Tooltip.Content>
        </Tooltip>
        {adornment}
        {editable && (
          <>
            <Tooltip>
              <Tooltip.Trigger
                render={
                  <button
                    ref={editButtonRef}
                    className={cn(
                      pageSecondaryHeaderBreadcrumbEditIconButtonStyles
                    )}
                    type="button"
                    onClick={() => setIsEditing(true)}
                  />
                }
              >
                <VisuallyHidden>Rename {children}</VisuallyHidden>
                <Icon name="edit" size="xs" />
              </Tooltip.Trigger>
              <Tooltip.Content>Rename</Tooltip.Content>
            </Tooltip>

            {isEditing && (
              <div className="absolute inset-0">
                <label className="sr-only" htmlFor={inputId}>
                  Rename {children}
                </label>
                <input
                  ref={inputRef}
                  className={cn(pageSecondaryHeaderBreadcrumbInputStyles)}
                  type="text"
                  id={inputId}
                  value={draft}
                  onBlur={commit}
                  onChange={event => setDraft(event.target.value)}
                  onKeyDown={event => {
                    if (event.key === "Enter") {
                      event.preventDefault()
                      commit()
                    } else if (event.key === "Escape") {
                      event.preventDefault()
                      cancel()
                    }
                  }}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
